import { useEffect, useMemo } from 'react';
import {
  generatePath,
  matchPath,
  useHistory,
  useLocation,
} from 'react-router-dom';
import { Button } from '@accure/ui/components/button/button-catalyst';
import { PlusIcon } from '@heroicons/react/24/outline';

import type { AlertNotificationRule } from '@/api/fe-api/domain/entities/alert-notifications';
import { GetRuleList } from '@/api/fe-api/view/queries/alert-notifications/get-rule-list';
import { FullPageContent } from '@/components/layout/full-page-content';
import { FormEditNotificationRule } from '@/components/pages/account/alert-notifications/form-edit-notification-rule/form-edit-notification-rule';
import { NotificationRule } from '@/components/pages/account/alert-notifications/notification-rule';
import { Dialog } from '@/components/ui/dialog';
import { useMe } from '@/hooks/use-me';

const pathToList = generatePath('/account/alert-notifications');
const pathToCreate = generatePath('/account/alert-notifications/new');
const pathToEdit = generatePath('/account/alert-notifications/edit');
export function AlertNotificationsPage() {
  const { data } = GetRuleList.useQuery();
  const { config } = useMe();

  const location = useLocation();
  const history = useHistory();

  const isCreateMode = Boolean(
    matchPath(location.pathname, {
      path: pathToCreate,
      exact: true,
    }),
  );

  const isEditMode = useMemo(
    () =>
      Boolean(
        matchPath(location.pathname, {
          path: pathToEdit,
          exact: true,
        }),
      ),
    [location.pathname],
  );

  function handleDialogClose() {
    history.push(pathToList);
  }

  function handleEditClick(rule: AlertNotificationRule) {
    history.push({
      pathname: pathToEdit,
      search: new URLSearchParams({
        id: rule.id,
      }).toString(),
    });
  }

  useEffect(
    function () {
      if (isEditMode) {
        const id = new URLSearchParams(location.search).get('id');

        if (!id) {
          history.push({ pathname: pathToCreate, search: undefined });
        }
      }
    },
    [isEditMode, location.search, history],
  );

  if (!data?.isOk()) {
    return null;
  }

  return (
    <FullPageContent
      testId="alert-notifications-page"
      scrollable
      className="bg-zinc-50"
    >
      <div className="container mx-auto my-8 max-w-5xl">
        <div className="mb-6 flex p-4">
          <div className="flex-1">
            <h1 className="mb-2 font-condensed text-2xl font-bold">
              Alert Notification Rules
            </h1>
            <p className="max-w-prose text-lg text-zinc-500">
              Configure when to receive email notifications based on{' '}
              {config.type === 'mobility'
                ? 'alert severity and frequency.'
                : 'alert severity, frequency, and selected assets.'}{' '}
              Choose to be notified immediately when new alerts are triggered or
              get a daily summary, and customize who should be notified for each
              rule.
            </p>
          </div>
          <div className="flex-0 flex w-60 items-end">
            <Button
              className="w-full bg-black py-6 text-white hover:bg-gray-800"
              to={pathToCreate}
            >
              <PlusIcon className="mr-2 size-4" />
              New Notification Rule
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          {data.value.length === 0 ? (
            <p className="flex h-24 items-center justify-center rounded-xl border border-dashed border-zinc-400/20 text-center italic text-zinc-500">
              Create a new notification rule to get started.
            </p>
          ) : (
            data.value
              .sort((a, b) => a.description.localeCompare(b.description))
              .map((rule) => (
                <NotificationRule
                  key={rule.id}
                  data={rule}
                  onEditClick={handleEditClick}
                />
              ))
          )}
        </div>
      </div>
      <Dialog open={isCreateMode || isEditMode} onClose={handleDialogClose}>
        <FormEditNotificationRule
          loadedData={
            (isEditMode &&
              data.value.find(
                (rule) =>
                  rule.id === new URLSearchParams(location.search).get('id'),
              )) ||
            undefined
          }
        />
      </Dialog>
    </FullPageContent>
  );
}
